import { useEffect, useRef, useState } from 'react';
import { ApiError, getCurrentRound, postSubmission, spotifySearch } from '../core/api';
import { useT } from '../core/lexicon';
import { useAsync } from '../core/useAsync';
import { countdown } from '../core/util';
import { HashAvatar } from '../core/sprite';
import { Screen, TopBar, CenterNote } from './Layout';
import type { CurrentRound, SpotifyResult } from '../core/types';

/** Debounced Spotify search (300ms, api.md). Returns rows already shaped for
 *  submission. Blank query clears. */
function useSearch() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<SpotifyResult[]>([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(timer.current);
    const v = q.trim();
    if (!v) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    timer.current = window.setTimeout(async () => {
      try {
        setRows(await spotifySearch(v));
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer.current);
  }, [q]);

  return { q, setQ, rows, loading };
}

function Art({ row }: { row: SpotifyResult }) {
  if (row.thumbnail_url) return <img className="thumb" src={row.thumbnail_url} alt="" />;
  return <HashAvatar seed={row.media_ref} size={44} />;
}

export function Submit() {
  const t = useT();
  const round = useAsync<CurrentRound | null>(getCurrentRound, []);
  const main = useSearch();
  const pred = useSearch();

  const [picked, setPicked] = useState<SpotifyResult | null>(null);
  const [predPicked, setPredPicked] = useState<SpotifyResult | null>(null);
  const [callMode, setCallMode] = useState<'ask' | 'no' | 'yes'>('ask');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<SpotifyResult | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const r = round.data ?? null;

  async function send() {
    if (!r || !picked) return;
    setBusy(true);
    setProblem(null);
    try {
      await postSubmission(r.round_id, picked.media_ref, predPicked?.media_ref);
      setSent(picked);
    } catch (e) {
      setProblem((e as ApiError).message || 'Could not submit.');
      setBusy(false);
    }
  }

  if (round.loading) {
    return (
      <Screen>
        <TopBar title={t('phase.submit.name')} />
        <CenterNote>
          <div className="spin">LOADING…</div>
        </CenterNote>
      </Screen>
    );
  }

  const header = (
    <TopBar
      title={r ? t('round.label', { n: r.sequence_no }) : t('phase.submit.name')}
      sub={t('phase.submit.name')}
      right={r ? countdown(r.submission_deadline) ?? undefined : undefined}
    />
  );

  // Already sent, or the round isn't accepting submissions.
  if (sent) {
    return (
      <Screen>
        {header}
        <CenterNote title={t('phase.submit.done').toUpperCase()}>
          <p style={{ fontSize: 18, fontWeight: 700 }}>{sent.title}</p>
          <p className="errline" style={{ background: 'transparent', color: 'var(--k1)' }}>{sent.subtitle}</p>
          <p>{t('phase.submit.prompt')}</p>
        </CenterNote>
      </Screen>
    );
  }
  if (!r || r.phase !== 'SUBMITTING') {
    return (
      <Screen>
        {header}
        <CenterNote title={t('phase.submit.closed').toUpperCase()}>
          <p>{r?.you?.submitted ? t('phase.submit.done') : t('empty.no_round')}</p>
        </CenterNote>
      </Screen>
    );
  }
  if (r.you?.submitted) {
    return (
      <Screen>
        {header}
        <CenterNote title={t('phase.submit.done').toUpperCase()}>
          <p>{t('phase.submit.prompt')}</p>
        </CenterNote>
      </Screen>
    );
  }

  return (
    <Screen>
      {header}
      <div className="theme">{t('round.theme').toUpperCase()} — {r.theme.toUpperCase()}</div>

      <div className="search">
        <div className="field">
          <input
            value={main.q}
            autoComplete="off"
            spellCheck={false}
            placeholder={t('phase.submit.prompt')}
            onChange={(e) => setMainQuery(e.target.value)}
          />
          <button onClick={() => setMainQuery('')}>X</button>
        </div>
        <p className="hint">{t('phase.submit.prompt')} — {t('duplicate.flag').toLowerCase()}s are found at close, never now.</p>
      </div>

      <div className="list">
        {main.loading ? (
          <div className="empty"><div className="spin">…</div></div>
        ) : main.q && main.rows.length === 0 ? (
          <div className="empty">
            <h3>NOTHING FOUND</h3>
            <p>Try the artist name too.</p>
          </div>
        ) : (
          main.rows.map((row) => (
            <button
              key={row.media_ref}
              className={`res${picked?.media_ref === row.media_ref ? ' on' : ''}`}
              onClick={() => setPicked(row)}
            >
              <Art row={row} />
              <span>
                <span className="t2">{row.title}</span>
                <span className="a">{row.subtitle}</span>
              </span>
            </button>
          ))
        )}
      </div>

      {picked ? (
        <div className="act">
          {callMode === 'ask' ? (
            <div className="callbox">
              <h3>{t('phase.predict.name').toUpperCase()}?</h3>
              <p>{t('phase.predict.hint', { n: t('phase.predict.hit') })}</p>
              <div className="callbtns">
                <button className="yes" onClick={() => setCallMode('yes')}>YES</button>
                <button
                  onClick={() => {
                    setCallMode('no');
                    setPredPicked(null);
                    pred.setQ('');
                  }}
                >
                  NO
                </button>
              </div>
            </div>
          ) : callMode === 'no' ? (
            <p className="hint callnote">
              {t('phase.predict.none')} · <a onClick={() => setCallMode('ask')}>change</a>
            </p>
          ) : (
            <div className="callsearch">
              <div className="calltop">
                <span className="calllabel">{t('phase.predict.name').toUpperCase()}</span>
                <a
                  onClick={() => {
                    setCallMode('ask');
                    setPredPicked(null);
                    pred.setQ('');
                  }}
                >
                  skip
                </a>
              </div>
              {predPicked ? (
                <div className="chosen" style={{ marginBottom: 0 }}>
                  <Art row={predPicked} />
                  <div>
                    <div className="t2">{predPicked.title}</div>
                    <div className="a">{predPicked.subtitle}</div>
                  </div>
                  <button className="calledit" aria-label="Change the call" onClick={() => setPredPicked(null)}>
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <div className="field">
                    <input
                      value={pred.q}
                      autoComplete="off"
                      spellCheck={false}
                      placeholder={t('phase.predict.name')}
                      onChange={(e) => pred.setQ(e.target.value)}
                    />
                    <button onClick={() => pred.setQ('')}>X</button>
                  </div>
                  {pred.rows.slice(0, 5).map((row) => (
                    <button key={row.media_ref} className="res" onClick={() => setPredPicked(row)}>
                      <Art row={row} />
                      <span>
                        <span className="t2">{row.title}</span>
                        <span className="a">{row.subtitle}</span>
                      </span>
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          <div className="chosen">
            <Art row={picked} />
            <div>
              <div className="t2">{picked.title}</div>
              <div className="a">{picked.subtitle}</div>
            </div>
          </div>
          {problem ? <div className="errline" style={{ marginBottom: 8 }}>{problem}</div> : null}
          <button className="go" disabled={busy} onClick={send}>
            {busy ? '…' : t('phase.submit.cta').toUpperCase()}
          </button>
        </div>
      ) : null}
    </Screen>
  );

  function setMainQuery(v: string) {
    main.setQ(v);
    if (!v) setPicked(null);
  }
}
